import { useQuery } from '@tanstack/react-query'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { DynamicBondingCurveClient } from '@meteora-ag/dynamic-bonding-curve-sdk'
import { connection, DBC_CONFIG_KEY, IS_RPC_CONFIGURED } from '@/lib/solana'
import { supabase } from '@/lib/supabase'
import { captureError } from '@/lib/sentry'

export interface DbcConfigValues {
  // On-chain config values (raw)
  migrationQuoteThresholdLamports: number
  migrationFeePercentage: number
  creatorTradingFeePercentage: number
  creatorMigrationFeePercentage: number

  // Derived values for revenue calculation
  migrationQuoteThresholdSOL: number
  migrationFeePerGraduationSOL: number
  protocolMigrationShare: number
  protocolTradingShareBC: number
  tradingFeeRate: number
}

async function fetchDbcConfig(): Promise<DbcConfigValues> {
  if (!IS_RPC_CONFIGURED) {
    return {
      migrationQuoteThresholdLamports: 0,
      migrationFeePercentage: 0,
      creatorTradingFeePercentage: 0,
      creatorMigrationFeePercentage: 0,
      migrationQuoteThresholdSOL: 0,
      migrationFeePerGraduationSOL: 0,
      protocolMigrationShare: 0,
      protocolTradingShareBC: 0,
      tradingFeeRate: 0,
    }
  }

  const dbcClient = new DynamicBondingCurveClient(connection, 'confirmed')
  const poolConfig = await dbcClient.state.getPoolConfig(DBC_CONFIG_KEY)

  if (!poolConfig) {
    throw new Error('DBC config account not found')
  }

  // Read fee parameters from on-chain config
  const migrationQuoteThresholdLamports = Number(poolConfig.migrationQuoteThreshold)
  const migrationFeePercentage = poolConfig.migrationFeePercentage
  const creatorTradingFeePercentage = poolConfig.creatorTradingFeePercentage
  const creatorMigrationFeePercentage = poolConfig.creatorMigrationFeePercentage

  // Read fee_rate_bps from pool_stats (populated by sync-pools from on-chain data)
  let feeRateBps = 100 // default 1%, overridden by DB
  try {
    const { data } = await supabase
      .from('pool_stats')
      .select('fee_rate_bps')
      .not('fee_rate_bps', 'is', null)
      .limit(1)
      .single()
    if (data?.fee_rate_bps) {
      feeRateBps = data.fee_rate_bps
    }
  } catch {
    // Fall through to default, non-critical
  }

  const migrationQuoteThresholdSOL = migrationQuoteThresholdLamports / LAMPORTS_PER_SOL
  const protocolMigrationShare = (100 - creatorMigrationFeePercentage) / 100
  const migrationFeePerGraduationSOL =
    migrationQuoteThresholdSOL * (migrationFeePercentage / 100) * protocolMigrationShare
  const protocolTradingShareBC = (100 - creatorTradingFeePercentage) / 100
  const tradingFeeRate = feeRateBps / 10_000

  return {
    migrationQuoteThresholdLamports,
    migrationFeePercentage,
    creatorTradingFeePercentage,
    creatorMigrationFeePercentage,
    migrationQuoteThresholdSOL,
    migrationFeePerGraduationSOL,
    protocolMigrationShare,
    protocolTradingShareBC,
    tradingFeeRate,
  }
}

export function useDbcConfig() {
  return useQuery({
    queryKey: ['dbc-config'],
    queryFn: async () => {
      try {
        return await fetchDbcConfig()
      } catch (error) {
        captureError(
          error instanceof Error ? error : new Error('Failed to fetch DBC config'),
          { feature: 'admin_dashboard', component: 'useDbcConfig' }
        )
        throw error
      }
    },
    staleTime: 5 * 60 * 1000, // 5 min, config rarely changes
    refetchInterval: 10 * 60 * 1000, // 10 min
    retry: 2,
  })
}

// Standalone fetch for use inside other queryFns (not a hook)
export { fetchDbcConfig }
